import React from "react";
import { Row, Col, Card, Badge } from "antd";
import { StarOutlined } from "@ant-design/icons";
import Scrollbars from "react-custom-scrollbars";
import InfiniteScroll from "react-infinite-scroll-component";
import defaultImage from "../../../../assets/images/no-image.svg";

const MenuProductGrid = ({
  loading,
  productSearchInput,
  saleTypeProducts,
  selectedCategory,
  cart,
  showImage,
  currenciesList,
  pricePrecision,
  getFoodTypeIcon,
  getColorFromId,
  handleAddOnModal,
  pickProduct,
  setSelectedProduct,
  getAddonSignature,
  setAddonSourceItem,
  setAddonRepeatDisabled,
  setShowAddonsCustomizePopUp,
  setAddOnsList,
}) => {
  const cardSpan = cart.items.length > 0 ? 6 : 4;

  // Same base-list rule as Retail/SuperMarket: while searching, match across
  // every category; otherwise scope to the selected category.
  const baseProducts =
    productSearchInput.length > 0
      ? saleTypeProducts.filter((product) => {
          const searchInput = productSearchInput.toLowerCase();
          const matchesName = product.name?.toLowerCase().includes(searchInput);
          const matchesValue = product.value?.toLowerCase().includes(searchInput);
          const matchesUPC = product.upc?.toLowerCase().includes(searchInput);
          const matchesAlias = product?.productAliasName?.toLowerCase().includes(searchInput);
          return matchesName || matchesValue || matchesUPC || matchesAlias;
        })
      : saleTypeProducts.filter((product) => product.mProductCategoryId === selectedCategory);

  // Additive Best Sellers / All Products split — mirrors Retail/SuperMarket's
  // frequentProducts / nonfrequentProducts grouping (never replaces the list).
  const frequentProducts = baseProducts.filter((product) => product.isBestSeller);
  const nonfrequentProducts = baseProducts.filter((product) => !product.isBestSeller);

  const handleProductClick = (product, e) => {
    setSelectedProduct(product);
    if (product?.productAddons?.length > 0 && product?.showAddons === "Y") {
      const productAddonLines = cart.items.filter(
        (item) => item.mProductId === product.mProductId && !item.isReturn && item.selectedAddons?.length > 0
      );
      if (productAddonLines.length > 0) {
        const requiredListFilter = (product.productAddons || []).filter((item) => item.mAddonGroup.minqty === 1);
        const optionsListFilter = (product.productAddons || []).filter((item) => item.mAddonGroup.minqty === 0);
        setAddOnsList({ requiredList: requiredListFilter, optionsList: optionsListFilter });

        const existingAddonSignatures = productAddonLines.map((line) => getAddonSignature(line.selectedAddons));
        const allSameAddons = existingAddonSignatures.every((sig) => sig === existingAddonSignatures[0]);
        if (allSameAddons) {
          setAddonSourceItem(productAddonLines[0]);
          setAddonRepeatDisabled(false);
        } else {
          setAddonSourceItem(productAddonLines[0]);
          setAddonRepeatDisabled(true);
        }
        const firstLine = productAddonLines[0];
        if (firstLine?.order === "Y") {
          handleAddOnModal(product, e);
        } else {
          setShowAddonsCustomizePopUp(true);
        }
      } else {
        handleAddOnModal(product, e);
      }
    } else {
      pickProduct(product);
    }
  };

  const renderProductCard = (product) => {
    const productKey = product.mProductId || product.id || crypto.randomUUID();
    return (
      <Col key={productKey} span={cardSpan}>
        <Card
          className="menu-product-card"
          onClick={(e) => handleProductClick(product, e)}
          style={
            showImage
              ? {
                  borderRadius: "8px",
                  border: "1px solid #e8e8e8",
                  cursor: "pointer",
                  position: "relative",
                  padding: "none",
                  overflow: "visible",
                }
              : {
                  height: "75px",
                  borderRadius: "10px",
                  border: "1px solid #e8e8e8",
                  padding: "10px",
                  cursor: "pointer",
                  color: "#0F0718",
                  fontWeight: "500",
                  backgroundColor: getColorFromId(product.mProductId || product.id),
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  position: "relative",
                }
          }
        >
          {showImage ? (
            <>
              {/* IMAGE BOX */}
              <div
                className="menu-product-card-image"
                style={{
                  height: "60px",
                  background: "#f5f5f5",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "8px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <img
                  src={product.imageurl || defaultImage}
                  alt={product?.productAliasName ? product?.productAliasName : product.name}
                  style={{
                    width: product.imageurl ? "100%" : "40px",
                    height: product.imageurl ? "100%" : "40px",
                    objectFit: product.imageurl ? "cover" : "contain",
                    margin: product.imageurl ? 0 : "auto",
                  }}
                />
                {product.isBestSeller && (
                  <div
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      backgroundColor: "#eab308",
                      padding: "2px 6px",
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "10px",
                      fontSize: "10px",
                      fontWeight: 500,
                      zIndex: 2,
                    }}
                  >
                    <StarOutlined style={{ fontSize: "10px", marginRight: "4px" }} />
                    Best
                  </div>
                )}
              </div>

              {/* TEXT SECTION */}
              <div className="menu-product-card-body" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h3
                  title={product?.productAliasName ? product?.productAliasName : product.name}
                  style={{
                    fontSize: "0.9em",
                    fontWeight: 600,
                    color: "#0F0718",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    display: "-webkit-box",
                    minHeight: "38px",
                    margin: 0,
                  }}
                >
                  {product?.productAliasName ? product?.productAliasName : product.name}
                </h3>

                {product.value && (
                  <div style={{ fontSize: "0.8rem", color: "#4b5563", marginBottom: "0.25rem" }}>
                    <span style={{ fontWeight: 500 }}>{product.value}</span>
                  </div>
                )}

                <div className="menu-product-card-price-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: "1em",
                      fontWeight: 600,
                      color: "#389e0d",
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      maxWidth: "100%",
                    }}
                  >
                    {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                    {product.sunitprice?.toFixed(pricePrecision ?? 2)}
                    {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                    {product.uomName && (
                      <span
                        title={product.uomName}
                        style={{
                          maxWidth: "55px",
                          whiteSpace: "nowrap",
                          color: "#389e0d",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontWeight: 600,
                          fontSize: "1em",
                        }}
                      >
                        /{product.uomName}
                      </span>
                    )}
                  </span>
                  {getFoodTypeIcon(product.foodType) && (
                    <img src={getFoodTypeIcon(product.foodType)} style={{ height: "18px" }} alt="food type" />
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* MINIMAL CARD */}
              <p
                title={product?.productAliasName ? product?.productAliasName : product.name}
                style={{
                  fontWeight: 500,
                  fontSize: "13px",
                  margin: 0,
                  lineHeight: "1.4",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  padding: "0 4px",
                }}
              >
                {product?.productAliasName ? product?.productAliasName : product.name}
              </p>
              <p
                style={{
                  color: "#6B7280",
                  fontWeight: 500,
                  fontSize: "12px",
                  margin: 0,
                  position: "absolute",
                  bottom: "8px",
                  left: "10px",
                }}
              >
                {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                {product.sunitprice?.toFixed(pricePrecision ?? 2)}
                {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
              </p>
            </>
          )}
        </Card>
      </Col>
    );
  };

  const renderSection = (title, products) => {
    if (products.length === 0) return null;
    return (
      <div style={{ marginBottom: "1.5vh" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 1vh 0.5vw" }}>
          <h2 style={{ fontSize: "1.1em", fontWeight: 500, color: "#0F0718", margin: 0 }}>{title}</h2>
          <Badge
            count={products.length}
            overflowCount={9999}
            style={{
              backgroundColor: "#f5f5f5",
              color: "#0F0718",
              fontSize: "0.8em",
              padding: "0px 8px",
              borderRadius: "10px",
            }}
          />
        </div>
        <Row className="menu-product-row" gutter={[10, 10]} style={{ marginLeft: "0.5vw", padding: "0 10px" }}>
          {products.map(renderProductCard)}
        </Row>
      </div>
    );
  };

  return (
    <div data-zone="products" data-zone-label="Products" tabIndex={-1} style={{ flex: 1 }}>
      <Scrollbars autoHide className="menu-product-scrollbar" style={{ height: "87vh" }}>
        <InfiniteScroll
          dataLength={frequentProducts.length + nonfrequentProducts.length}
          hasMore={true}
          endMessage={
            <p style={{ textAlign: "center" }}>
              <b>Yay! you have seen it all</b>
            </p>
          }
          style={{ overflowX: "hidden" }}
        >
          {loading ? (
            <p style={{ textAlign: "center" }}>Loading products...</p>
          ) : (
            <>
              {renderSection("Best Sellers", frequentProducts)}
              {renderSection("All Products", nonfrequentProducts)}
            </>
          )}
        </InfiniteScroll>
      </Scrollbars>
    </div>
  );
};

export default React.memo(MenuProductGrid);
